import type { CmmdProjectMilestone } from "@cmmd-forge/contracts";
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleIcon,
  CircleMinusIcon,
  CircleXIcon,
  DiamondIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";

/*
 * Status is the board's column axis, which is one of the two places color is
 * still spent — but it spends the semantic tokens, not raw Tailwind hues. The
 * previous sky / violet / emerald / rose set duplicated `--info`, `--success`,
 * and `--destructive` at slightly different hues, so the same meaning rendered
 * one color here and another color three components away.
 */
export function ProjectTaskBoardStatusIcon({ status }: { readonly status: string }) {
  if (status === "in_progress") {
    return <CircleDashedIcon className="size-4 shrink-0 text-info-foreground" aria-hidden />;
  }
  if (status === "in_review") {
    return <CircleDotIcon className="size-4 shrink-0 text-warning-foreground" aria-hidden />;
  }
  if (status === "done") {
    return <CircleCheckIcon className="size-4 shrink-0 text-success-foreground" aria-hidden />;
  }
  if (status === "blocked") {
    return <CircleMinusIcon className="size-4 shrink-0 text-destructive-foreground" aria-hidden />;
  }
  if (status === "cancelled") {
    return <CircleXIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
  }
  return <CircleIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
}

export function ProjectTaskMilestoneIcon({
  status,
}: {
  readonly status: CmmdProjectMilestone["status"] | null;
}) {
  if (status == null) {
    return <DiamondIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
  }
  // One diamond per lane, so this is a cheap place to be quiet. `in_progress` was
  // amber — a fourth warm claim next to `--primary`, the old assignee ring, and the
  // old High chevron. It now separates by weight; only overdue and completed, the
  // two states a reader has to act on, keep a color.
  const tone =
    status === "overdue"
      ? "text-destructive-foreground"
      : status === "completed"
        ? "text-success-foreground"
        : status === "in_progress"
          ? "text-foreground"
          : "text-muted-foreground";
  return <DiamondIcon className={cn("size-4 shrink-0", tone)} aria-hidden />;
}
